import { SubTradeRequestTypes } from '@generated/graphql';
import type { PortfolioReport } from '@interfaces/portfolioReport';
import type {
  ReviewTradeGroupSide,
  ReviewTradeGroupSummary,
  ReviewTradePortfolioGroup,
  ReviewTradeRow,
} from '../../types/tradeReview.types';
import { formatAuditAccountType } from '../presentation/accountTypeLabels';
import {
  getReportClientLabel,
  getReportGoalLabel,
} from '../presentation/reportLabels';
import { buildReviewTradeCounts } from './reviewTradeStatusAndRows';

export const getReviewTradeClientLabel = (
  row: Pick<ReviewTradeRow, 'subAccount'>,
): string => {
  const user = row.subAccount?.account?.user;
  if (user?.firstName && user?.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }

  return '';
};

export const groupReviewTradesByPortfolio = (
  translateBackendString: (translatedObject: unknown) => string,
  rows: ReviewTradeRow[],
  reportsById: Map<string, PortfolioReport>,
  unknownPortfolioLabel: string,
): ReviewTradePortfolioGroup[] => {
  const groups = new Map<string, ReviewTradePortfolioGroup>();

  for (const row of rows) {
    const portfolioReportId = row.portfolioReport || 'unknown';
    const existing = groups.get(portfolioReportId);
    if (existing) {
      existing.rows.push(row);
      continue;
    }

    const report = reportsById.get(portfolioReportId);
    groups.set(portfolioReportId, {
      portfolioReportId,
      clientLabel: report
        ? getReportClientLabel(report)
        : getReviewTradeClientLabel(row) || unknownPortfolioLabel,
      portfolioLabel: report ? getReportGoalLabel(report) : '',
      modelPortfolioId: report?.modelPortfolio?.id,
      modelLabel: report
        ? translateBackendString(report.modelPortfolio?.translatedName)
        : '',
      rows: [row],
    });
  }

  return [...groups.values()]
    .map((group) => ({
      ...group,
      rows: [...group.rows].sort((a, b) => {
        const tickerA = a.financialProduct?.ticker ?? '';
        const tickerB = b.financialProduct?.ticker ?? '';
        if (tickerA !== tickerB) {
          return tickerA.localeCompare(tickerB);
        }

        return a.type.localeCompare(b.type);
      }),
    }))
    .sort((a, b) => {
      const clientCompare = a.clientLabel.localeCompare(b.clientLabel);
      if (clientCompare !== 0) {
        return clientCompare;
      }

      return a.portfolioLabel.localeCompare(b.portfolioLabel);
    });
};

export const uniqueReviewTradeAccountTypeLabels = (
  rows: ReviewTradeRow[],
): string[] => {
  const labels: string[] = [];
  const seen = new Set<string>();

  for (const row of rows) {
    const raw = row.subAccount?.account?.type;
    if (!raw) {
      continue;
    }

    const label = formatAuditAccountType(raw);
    if (!label || label === '—' || seen.has(label)) {
      continue;
    }

    seen.add(label);
    labels.push(label);
  }

  return labels.sort((a, b) => a.localeCompare(b));
};

export const resolveReviewTradeGroupSide = (
  buyCents: number,
  sellCents: number,
): ReviewTradeGroupSide => {
  if (buyCents > 0 && sellCents > 0) {
    return 'BOTH';
  }

  if (buyCents > 0) {
    return 'BUY';
  }

  if (sellCents > 0) {
    return 'SELL';
  }

  return 'NONE';
};

export const summarizeReviewTradeGroup = (
  rows: ReviewTradeRow[],
): ReviewTradeGroupSummary => {
  const counts = buildReviewTradeCounts(rows);
  const amounts = rows.reduce(
    (summary, row) => {
      if (row.status === 'CANCELED') {
        return summary;
      }

      const amount = Math.abs(row.moneyAllocatedCents || 0);
      const isBuy = row.type === SubTradeRequestTypes.Buy;
      if (isBuy) {
        summary.buyCents += amount;
      } else {
        summary.sellCents += amount;
      }

      if (row.status === 'READY') {
        if (isBuy) {
          summary.readyBuyCents += amount;
        } else {
          summary.readySellCents += amount;
        }
      } else if (row.status === 'HELD') {
        if (isBuy) {
          summary.heldBuyCents += amount;
        } else {
          summary.heldSellCents += amount;
        }
      }

      return summary;
    },
    {
      buyCents: 0,
      sellCents: 0,
      readyBuyCents: 0,
      readySellCents: 0,
      heldBuyCents: 0,
      heldSellCents: 0,
    },
  );

  return {
    tradeCount: counts.total,
    buyCents: amounts.buyCents,
    sellCents: amounts.sellCents,
    readyBuyCents: amounts.readyBuyCents,
    readySellCents: amounts.readySellCents,
    heldBuyCents: amounts.heldBuyCents,
    heldSellCents: amounts.heldSellCents,
    side: resolveReviewTradeGroupSide(amounts.buyCents, amounts.sellCents),
    readyCount: counts.ready,
    heldCount: counts.held,
    pendingCount: counts.pending,
    canceledCount: counts.canceled,
  };
};

export const resolveReviewTradeGroupDisplayAmounts = (
  summary: ReviewTradeGroupSummary,
): { buyCents: number; sellCents: number } => {
  const useSettledAmounts = summary.readyCount > 0 || summary.heldCount > 0;
  if (useSettledAmounts) {
    return {
      buyCents: summary.readyBuyCents + summary.heldBuyCents,
      sellCents: summary.readySellCents + summary.heldSellCents,
    };
  }

  return { buyCents: summary.buyCents, sellCents: summary.sellCents };
};

export type ReviewTradeStatusFilter =
  | 'all'
  | 'ready'
  | 'held'
  | 'pending'
  | 'canceled'
  | 'needsApproval';

export const matchesReviewTradeSearch = (
  translateBackendString: (translatedObject: unknown) => string,
  group: Pick<ReviewTradePortfolioGroup, 'clientLabel' | 'portfolioLabel'>,
  row: Pick<ReviewTradeRow, 'financialProduct'>,
  query: string,
): boolean => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return true;
  }

  const haystack = [
    group.clientLabel,
    group.portfolioLabel,
    row.financialProduct?.ticker,
    translateBackendString(row.financialProduct?.translatedName),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return haystack.includes(normalized);
};

export const matchesReviewTradeStatusFilter = (
  row: Pick<ReviewTradeRow, 'status' | 'isActionable' | 'isVerified'>,
  filter: ReviewTradeStatusFilter,
): boolean => {
  if (filter === 'all') {
    return true;
  }

  if (filter === 'needsApproval') {
    return row.isActionable && !row.isVerified;
  }

  if (filter === 'pending') {
    return row.status === 'INITIATED';
  }

  return row.status === filter.toUpperCase();
};

export const buildReviewTradeFilterIndex = (
  groups: ReviewTradePortfolioGroup[],
  activeFilters: {
    modelPortfolioId?: string;
    financialProductId?: string;
  } = {},
): {
  modelOptions: Array<{ id: string; label: string; count: number }>;
  assetOptions: Array<{
    financialProductId: string;
    ticker: string;
    count: number;
  }>;
} => {
  const groupsForModels = activeFilters.financialProductId
    ? groups.filter((group) =>
        group.rows.some(
          (row) =>
            row.financialProduct?.id === activeFilters.financialProductId,
        ),
      )
    : groups;
  const groupsForAssets = activeFilters.modelPortfolioId
    ? groups.filter(
        (group) => group.modelPortfolioId === activeFilters.modelPortfolioId,
      )
    : groups;

  const modelCounts = new Map<string, { label: string; count: number }>();
  for (const group of groupsForModels) {
    if (!group.modelPortfolioId) {
      continue;
    }

    const existing = modelCounts.get(group.modelPortfolioId);
    if (existing) {
      existing.count += 1;
    } else {
      modelCounts.set(group.modelPortfolioId, {
        label: group.modelLabel || group.modelPortfolioId,
        count: 1,
      });
    }
  }

  const assetReportIds = new Map<
    string,
    { ticker: string; reportIds: Set<string> }
  >();
  for (const group of groupsForAssets) {
    const productIds = new Set<string>();
    for (const row of group.rows) {
      const productId = row.financialProduct?.id;
      const ticker = row.financialProduct?.ticker;
      if (!productId || !ticker || productIds.has(productId)) {
        continue;
      }

      productIds.add(productId);
      const existing = assetReportIds.get(productId);
      if (existing) {
        existing.reportIds.add(group.portfolioReportId);
      } else {
        assetReportIds.set(productId, {
          ticker,
          reportIds: new Set([group.portfolioReportId]),
        });
      }
    }
  }

  return {
    modelOptions: [...modelCounts.entries()]
      .map(([id, value]) => ({
        id,
        label: value.label,
        count: value.count,
      }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
    assetOptions: [...assetReportIds.entries()]
      .map(([financialProductId, value]) => ({
        financialProductId,
        ticker: value.ticker,
        count: value.reportIds.size,
      }))
      .sort((a, b) => b.count - a.count || a.ticker.localeCompare(b.ticker)),
  };
};

export const filterReviewTradeGroups = (
  translateBackendString: (translatedObject: unknown) => string,
  groups: ReviewTradePortfolioGroup[],
  filters: {
    searchQuery?: string;
    statusFilter?: ReviewTradeStatusFilter;
    modelPortfolioId?: string;
    financialProductId?: string;
  },
): ReviewTradePortfolioGroup[] => {
  const statusFilter = filters.statusFilter ?? 'all';
  const searchQuery = filters.searchQuery ?? '';
  const hasSearch = searchQuery.trim().length > 0;
  const hasStatusFilter = statusFilter !== 'all';
  const hasModelFilter = Boolean(filters.modelPortfolioId);
  const hasAssetFilter = Boolean(filters.financialProductId);

  if (!hasSearch && !hasStatusFilter && !hasModelFilter && !hasAssetFilter) {
    return groups;
  }

  return groups
    .filter(
      (group) =>
        !hasModelFilter || group.modelPortfolioId === filters.modelPortfolioId,
    )
    .map((group) => ({
      ...group,
      rows: group.rows.filter(
        (row) =>
          matchesReviewTradeStatusFilter(row, statusFilter) &&
          matchesReviewTradeSearch(
            translateBackendString,
            group,
            row,
            searchQuery,
          ) &&
          (!hasAssetFilter ||
            row.financialProduct?.id === filters.financialProductId),
      ),
    }))
    .filter((group) => group.rows.length > 0);
};

export const hasActiveReviewTradeFilters = (filters: {
  searchQuery?: string;
  statusFilter?: ReviewTradeStatusFilter;
  modelPortfolioId?: string;
  financialProductId?: string;
}): boolean =>
  Boolean(filters.searchQuery?.trim()) ||
  (filters.statusFilter != null && filters.statusFilter !== 'all') ||
  Boolean(filters.modelPortfolioId) ||
  Boolean(filters.financialProductId);
